"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { setSessionExpiredHandler } from "@project-x/auth";

/** Browser-safe Consumer identity returned by the opaque-session gateway. */
export interface ConsumerUser {
  id: string;
  encodedId?: string;
  sub?: string;
  email?: string;
  username?: string;
  displayName?: string;
  name?: string;
  status?: string;
  roles: string[];
  profileComplete?: boolean;
  emailVerified?: boolean;
}

/** Login options retained for compatibility with existing Consumer call sites. */
export interface ConsumerLoginOptions {
  returnTo?: string;
  idpHint?: "google" | "apple";
}

/** Safe error shape rendered by the existing login page. */
export interface ConsumerAuthError {
  message?: string;
  error_description?: string;
}

interface ConsumerSessionContextValue {
  user: ConsumerUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: ConsumerAuthError | null;
  refreshSession: () => Promise<ConsumerUser | null>;
  login: (options?: string | ConsumerLoginOptions) => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: readonly string[]) => boolean;
  hasAllRoles: (roles: readonly string[]) => boolean;
  isAdmin: boolean;
  isManager: boolean;
}

const ConsumerSessionContext = createContext<ConsumerSessionContextValue | null>(null);

const PUBLIC_CONSUMER_PATHS = [
  "/", "/events", "/faq", "/contact", "/terms", "/privacy", "/account-deletion", "/users",
  "/auth/login", "/auth/recover", "/auth/reset-password", "/auth/logout/callback", "/register", "/verify-email",
] as const;

/** Returns true only for pages intentionally readable without a Consumer session. */
export function isPublicConsumerPath(pathname: string): boolean {
  return PUBLIC_CONSUMER_PATHS.some((path) => pathname === path || (path !== "/" && pathname.startsWith(`${path}/`)));
}

/** Restricts login destinations to local, non-callback application paths. */
export function sanitizeConsumerReturnTo(value: string | null | undefined): string {
  if (
    !value || value.length > 2_048 || !value.startsWith("/") || value.startsWith("//") || value.includes("\\") ||
    /[\r\n]/.test(value) || value.startsWith("/auth/callback") || value.startsWith("/auth/login")
  ) return "/events/";
  return value;
}

/** Validates the minimal session shape before it reaches React components. */
export function parseConsumerUser(value: unknown): ConsumerUser | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.id !== "string" || candidate.id.length === 0 || candidate.id.length > 128) return null;
  if (!Array.isArray(candidate.roles) || candidate.roles.some((role) => typeof role !== "string" || role.length > 128)) return null;
  const parsed: ConsumerUser = { id: candidate.id, roles: [...candidate.roles] as string[] };
  for (const key of ["encodedId", "sub", "email", "username", "displayName", "name", "status"] as const) {
    const field = candidate[key];
    if (typeof field === "string" && field.length <= 512) parsed[key] = field;
  }
  if (typeof candidate.profileComplete === "boolean") parsed.profileComplete = candidate.profileComplete;
  if (typeof candidate.emailVerified === "boolean") parsed.emailVerified = candidate.emailVerified;
  return parsed;
}

/** Redirects protected static pages only after session bootstrap is complete. */
function StaticConsumerGuard({ children, session }: PropsWithChildren<{ session: ConsumerSessionContextValue }>) {
  const pathname = usePathname();
  const router = useRouter();
  const isPublic = isPublicConsumerPath(pathname);

  useEffect(() => {
    if (!session.isLoading && !session.isAuthenticated && !isPublic) {
      router.replace(`/auth/login/?returnTo=${encodeURIComponent(`${pathname}${window.location.search}`)}`);
    }
  }, [isPublic, pathname, router, session.isAuthenticated, session.isLoading]);

  if (!isPublic && session.isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center" aria-label="Cargando sesión">
        <div className="w-10 h-10 border-4 border-gruv-pink border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isPublic && !session.isAuthenticated) return null;
  return children;
}

/** Provides opaque Consumer session state; Keycloak tokens remain server-only. */
export function AuthSessionProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<ConsumerUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ConsumerAuthError | null>(null);
  const userRef = useRef<ConsumerUser | null>(null);
  userRef.current = user;

  const loadSession = useCallback(async (synchronize: boolean): Promise<ConsumerUser | null> => {
    try {
      const response = await fetch(synchronize ? "/api/auth/refresh" : "/api/auth/session", {
        method: synchronize ? "POST" : "GET",
        credentials: "include",
        cache: "no-store",
        ...(synchronize ? { headers: { "content-type": "application/json" }, body: "{}" } : {}),
      });
      if (!response.ok) {
        setUser(null);
        setError({ message: response.status === 401 ? "not_authenticated" : "session_unavailable" });
        return null;
      }
      const payload = (await response.json()) as { authenticated?: boolean; user?: unknown };
      const authenticatedUser = payload.authenticated ? parseConsumerUser(payload.user) : null;
      if (payload.authenticated && !authenticatedUser) {
        setUser(null);
        setError({ message: "invalid_session" });
        return null;
      }
      const nextUser = authenticatedUser ? { ...authenticatedUser, name: authenticatedUser.displayName } : null;
      setUser(nextUser);
      setError(null);
      return nextUser;
    } catch {
      setUser(null);
      setError({ message: "session_unavailable" });
      return null;
    }
  }, []);

  const refreshSession = useCallback(() => loadSession(true), [loadSession]);

  useEffect(() => {
    let active = true;
    void loadSession(false).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [loadSession]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      const wasAuthenticated = !!userRef.current;
      setUser(null);
      setError({ message: "session_expired" });
      if (!wasAuthenticated || typeof window === "undefined") return;
      const next = `${window.location.pathname}${window.location.search}`;
      window.location.assign(`/auth/login/?returnTo=${encodeURIComponent(sanitizeConsumerReturnTo(next))}`);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async (options?: string | ConsumerLoginOptions): Promise<void> => {
    const normalized = typeof options === "string" ? { returnTo: options } : options ?? {};
    const params = new URLSearchParams({ returnTo: sanitizeConsumerReturnTo(normalized.returnTo) });
    if (normalized.idpHint) params.set("idpHint", normalized.idpHint);
    window.location.assign(`/api/auth/authorize?${params.toString()}`);
  }, []);

  const logout = useCallback(() => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/api/auth/logout";
    // Static HTML is served with Referrer-Policy: no-referrer; send origin on this POST for CSRF fallback.
    form.referrerPolicy = "origin";
    form.hidden = true;
    document.body.appendChild(form);
    form.submit();
  }, []);

  const value = useMemo<ConsumerSessionContextValue>(() => {
    const roles = user?.roles ?? [];
    return {
      user, isLoading, isAuthenticated: !!user, error, refreshSession, login, logout,
      hasRole: (role) => roles.includes(role),
      hasAnyRole: (required) => required.some((role) => roles.includes(role)),
      hasAllRoles: (required) => required.every((role) => roles.includes(role)),
      isAdmin: roles.includes("admin"),
      isManager: roles.some((role) => role === "manager" || role.startsWith("manager:")),
    };
  }, [error, isLoading, login, logout, refreshSession, user]);

  return (
    <ConsumerSessionContext.Provider value={value}>
      <StaticConsumerGuard session={value}>{children}</StaticConsumerGuard>
    </ConsumerSessionContext.Provider>
  );
}

/** Returns the Consumer opaque-session context. */
export function useAuth(): ConsumerSessionContextValue {
  const context = useContext(ConsumerSessionContext);
  if (!context) throw new Error("useAuth must be used inside AuthSessionProvider");
  return context;
}

/** Returns whether the current Consumer session is authenticated. */
export function useIsAuthenticated(): boolean {
  return useAuth().isAuthenticated;
}

/** Props for the local token-free authentication gate. */
export interface AuthGateProps extends PropsWithChildren {
  fallback?: ReactNode;
  unauthorized?: ReactNode;
  roles?: readonly string[];
  requireAllRoles?: boolean;
  redirectTo?: string;
  onUnauthorized?: () => void;
}

/** Gates a static subtree using the opaque Consumer session. */
export function AuthGate({ children, fallback, unauthorized, roles, requireAllRoles = false, redirectTo, onUnauthorized }: AuthGateProps): ReactNode {
  const auth = useAuth();
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && redirectTo !== undefined) void auth.login(window.location.pathname);
  }, [auth, redirectTo]);
  if (auth.isLoading) return fallback ?? null;
  if (!auth.isAuthenticated) return unauthorized ?? fallback ?? null;
  if (roles?.length) {
    const allowed = requireAllRoles ? auth.hasAllRoles(roles) : auth.hasAnyRole(roles);
    if (!allowed) {
      onUnauthorized?.();
      return unauthorized ?? <div className="p-8 text-center">Acceso denegado</div>;
    }
  }
  return children;
}

export default AuthSessionProvider;
